import React, { useEffect } from "react";
import { useSeo } from "../lib/useSeo";
import { PublicPageHeader, PublicPageFooter } from "./PublicPageChrome";
import { capture } from "../lib/analytics";
import "./landing/landing.css";

// /bidders — the landing page referenced by the physical mail campaign to
// named upset bidders (ADR 0033 Month 1: "5+ replies from 300 letters" by
// 2026-10-01). See docs/RELAUNCH.md and the case_bidders mail-export.
//
// Reuses the editorial .lp system from landing.css (the canonical direction
// per docs/design/DESIGN_SYSTEM.md) rather than the older .legal-content or
// app-token conventions other standalone pages use — this page's whole job
// is conversion, and someone arriving here just received physical mail with
// Parcely's name on it, so it should look like the same product, not a
// bolted-on tool page.
//
// noindex (useSeo below): this URL exists for one audience — mail
// recipients — and organic search traffic here would be noise at best and
// confusing out of context at worst ("we track your bid" reads very
// differently to someone who never got a letter).
//
// Deliberately NO new backend endpoint, NO new database write path, NO
// per-recipient personalization via query param. The letter's own CTA is
// "call or text"; this page's job is to let someone who got a letter verify
// Parcely is real and get the same two channels, plus the general free
// self-serve signup already in the header. If a lead-capture form turns out
// to be worth the extra scope, that's a deliberate follow-up, not something
// to fold in silently here.

// Read from env so a real number/inbox is never hardcoded into the bundle
// before it exists, and so the SAME build can be redeployed once it is set
// without a code change. Both channels degrade gracefully: an unset one is
// simply not rendered, rather than shipping a dead tel:/mailto: link.
// Read per render (CRA still inlines the values at build time) so tests can set them.
function contactChannels() {
  const phone = process.env.REACT_APP_BIDDER_PHONE || ""; // display form, e.g. "(704) 555-0100"
  return {
    RAW_PHONE: phone,
    PHONE_TEL: phone.replace(/[^\d+]/g, ""), // tel: wants digits (+ optional leading +)
    EMAIL: process.env.REACT_APP_BIDDER_EMAIL || "",
  };
}

const MAILTO_SUBJECT = encodeURIComponent("I got your letter");

const BiddersLandingPage: React.FC = () => {
  const { RAW_PHONE, PHONE_TEL, EMAIL } = contactChannels();
  useSeo({
    title: "For Active NC Foreclosure Bidders | Parcely",
    description:
      "You received a letter from Parcely about a North Carolina foreclosure auction. Here's what we track, and how to reach us.",
    noindex: true,
  });

  // This route's own existence is the attribution signal for the mailer — no
  // one reaches /bidders except from the letter or by someone forwarding it,
  // so a bare pageview count here already answers "did anyone respond,"
  // ahead of and independent of whichever channel (call/text/email) they use.
  useEffect(() => {
    capture("bidder_landing_view");
  }, []);

  const trackChannel = (channel: "phone" | "email") => () =>
    capture("bidder_landing_cta_click", { channel });

  const hasPhone = PHONE_TEL.length > 0;
  const hasEmail = EMAIL.length > 0;

  return (
    <div className="lp">
      <PublicPageHeader />
      <main id="main" tabIndex={-1}>
        <section id="sec-hero" style={{ padding: "56px 24px 48px" }}>
          <div style={{ maxWidth: 680, margin: "0 auto", textAlign: "center" }}>
            <span className="lp-kicker">For active NC foreclosure bidders</span>
            <h1 className="lp-h1" style={{ marginTop: 18 }}>
              You got a letter from us. Here&rsquo;s why.
            </h1>
            <p className="lp-hero-lede" style={{ margin: "22px auto 0", maxWidth: "100%" }}>
              We track every tax and mortgage foreclosure auction across North Carolina.
              We found your name and mailing address through your own upset-bid
              filing with the Clerk of Court &mdash; it&rsquo;s public record, and we&rsquo;d
              rather you check us out directly than take a letter&rsquo;s word for it.
            </p>

            {!hasPhone && !hasEmail && (
              <div className="lp-cta-row center">
                {/* No contact channel configured (REACT_APP_BIDDER_PHONE / _EMAIL):
                    never leave a letter recipient with nothing to click. */}
                <a className="lp-btn lp-btn-primary" href="/?auth=signup">
                  Get free access
                </a>
              </div>
            )}
            {(hasPhone || hasEmail) && (
              <div className="lp-cta-row center">
                {hasPhone && (
                  <a
                    className="lp-btn lp-btn-primary"
                    href={`tel:${PHONE_TEL}`}
                    onClick={trackChannel("phone")}
                  >
                    Call or text {RAW_PHONE}
                  </a>
                )}
                {hasEmail && (
                  <a
                    className="lp-btn lp-btn-outline"
                    href={`mailto:${EMAIL}?subject=${MAILTO_SUBJECT}`}
                    onClick={trackChannel("email")}
                  >
                    Email instead
                  </a>
                )}
              </div>
            )}
            <p className="lp-hero-trust">
              Ten minutes, no pitch &mdash; just questions. We&rsquo;ll set you up with free
              access either way.
            </p>
          </div>
        </section>

        <section className="lp-shell" style={{ padding: "8px 24px 48px" }}>
          <h2 className="lp-h2" style={{ textAlign: "center" }}>
            What you&rsquo;d actually be looking at
          </h2>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
              gap: 20,
              marginTop: 32,
            }}
          >
            <div className="lp-card" style={{ padding: 22 }}>
              <h3 style={{ fontSize: 17 }}>Every auction, statewide</h3>
              <p className="lp-muted" style={{ marginTop: 10, fontSize: 14.5, lineHeight: 1.6 }}>
                Scheduled and recent tax and mortgage foreclosure sales across North Carolina
                &mdash; not just what you find courthouse by courthouse.
              </p>
            </div>
            <div className="lp-card" style={{ padding: 22 }}>
              <h3 style={{ fontSize: 17 }}>Property and tax detail</h3>
              <p className="lp-muted" style={{ marginTop: 10, fontSize: 14.5, lineHeight: 1.6 }}>
                Assessed value, tax amount due, and parcel facts pulled together per listing, so
                it&rsquo;s not five sites and a spreadsheet.
              </p>
            </div>
            <div className="lp-card" style={{ padding: 22 }}>
              <h3 style={{ fontSize: 17 }}>Comparable sales, where we have them</h3>
              <p className="lp-muted" style={{ marginTop: 10, fontSize: 14.5, lineHeight: 1.6 }}>
                What similar properties nearby have actually sold for recently &mdash; not an
                algorithmic estimate.
              </p>
            </div>
          </div>
        </section>

        <section style={{ padding: "0 24px 64px" }}>
          <p
            className="lp-muted"
            style={{ maxWidth: 640, margin: "0 auto", fontSize: 14.5, lineHeight: 1.75, textAlign: "center" }}
          >
            We&rsquo;re a small North Carolina team, and we built this because we needed it
            ourselves. We&rsquo;re still early &mdash; this letter went to a few hundred active
            bidders across the state, and we&rsquo;d genuinely like to hear from someone
            who&rsquo;s actually out there bidding: what&rsquo;s useful, what&rsquo;s missing,
            what would be worth paying for.
          </p>
        </section>
      </main>
      <PublicPageFooter />
    </div>
  );
};

export default BiddersLandingPage;
